import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { AppLink } from '~/components/AppLink';
import { useAuthz } from '~/context/AuthzContext';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitDashboardWidgets } from '~/config/widgets/cockpit/cockpitDashboardWidgets';
import {
  CardLink,
  fmtChf,
  fmtDate,
  fmtTime,
  Kpi,
  PageHead,
  V3Page,
} from '~/components/cockpit/v3';
import {
  flattenActivities,
  type CockpitEvent,
  type WidgetProps,
} from '~/components/cockpit/cockpitData';
import { getCockpitActivityTitle } from '~/components/widget/cockpit/cockpitActivityDesignDataConfig';
import { useEffect, useRef } from 'react';
import { trackEvent } from '@agentselly/analytics';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME,
)({
  component: HomePage,
});

interface Contact {
  readonly firstname?: string;
  readonly lastname?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly mobile?: string;
}

const fullName = (c?: Contact): string =>
  [
    c?.firstname,
    c?.lastname,
  ]
    .filter(Boolean)
    .join(' ');

function ContactCard(props: {
  readonly title: string;
  readonly person?: Contact;
}): React.ReactElement | null {
  const name = fullName(props.person);
  if (!name) return null;
  const phone = props.person?.mobile || props.person?.phone;
  return (
    <section className="card">
      <div className="sec">
        <h2>{props.title}</h2>
      </div>
      <p
        className="t"
        style={{ fontWeight: 600 }}
      >
        {name}
      </p>
      {props.person?.email ? (
        <p>
          <a
            className="lnk"
            href={`mailto:${props.person.email}`}
          >
            {props.person.email}
          </a>
        </p>
      ) : null}
      {phone ? (
        <p>
          <a
            className="lnk"
            href={`tel:${phone}`}
          >
            {phone}
          </a>
        </p>
      ) : null}
    </section>
  );
}

function HomePage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');

  const trackedRef = useRef(false);
  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    trackEvent('immodossier cockpit opened');
  }, []);

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_HOME,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitDashboardWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const welcome = wp.cockpitWelcome ?? {};
  const property = welcome.property ?? {};
  const photos: string[] = wp.cockpitExpose?.photos ?? [];
  const visits: CockpitEvent[] = (wp.cockpitCalendarEventsWidget?.booked ?? [])
    .filter((e: CockpitEvent) => new Date(e.date).getTime() >= Date.now())
    .slice(0, 3);
  const activities = flattenActivities(wp.cockpitActivities?.activities).slice(
    0,
    5,
  );
  const dossierId = query.data.dossierId;
  const address = [
    property.address,
    [
      property.zip,
      property.city,
    ]
      .filter(Boolean)
      .join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <V3Page>
      <PageHead
        data-widget="cockpitWelcome"
        title={property.title || address || '–'}
        sub={address}
        end={
          <div className="row wrap">
            {welcome.lead?.status ? (
              <span className="chip c-info">{welcome.lead.status}</span>
            ) : null}
            {dossierId ? (
              <AppLink
                to={
                  AppRoute.$LANG_$COMPANYID_$CONTACTID_DOSSIER_$DOSSIERID_HOME
                }
                params={{ ...params, dossierId }}
                className="btn"
                data-od-id="cockpit-open-dossier"
              >
                {t('cockpit_home_open_dossier', 'Dossier ansehen')}
              </AppLink>
            ) : null}
          </div>
        }
      />

      <div className="grid g4">
        <Kpi
          data-widget="cockpitAdViews"
          value={wp.cockpitAdViews?.value ?? '–'}
          delta={wp.cockpitAdViews?.changeInLastWeek}
          label={t('cockpit_home_kpi_views', 'Inserat-Aufrufe')}
        />
        <Kpi
          data-widget="cockpitInterestedBuyers"
          value={wp.cockpitInterestedBuyers?.value ?? '–'}
          delta={wp.cockpitInterestedBuyers?.changeInLastWeek}
          label={t('cockpit_home_kpi_interest', 'Interessenten')}
        />
        <Kpi
          data-widget="cockpitBookedVisits"
          value={wp.cockpitBookedVisits?.value ?? '–'}
          label={t('cockpit_home_kpi_visits', 'Gebuchte Besichtigungen')}
        />
        <Kpi
          data-widget="cockpitOffers"
          value={wp.cockpitOffers?.value ?? '–'}
          delta={wp.cockpitOffers?.changeInLastWeek}
          label={t('cockpit_home_kpi_offers', 'Angebote')}
        />
      </div>

      <div className="cols">
        <div className="stack">
          <section
            className="card"
            data-widget="cockpitCalendarEventsWidget"
          >
            <div className="sec">
              <h2>{t('cockpit_home_next_visits', 'Nächste Besichtigungen')}</h2>
              <CardLink to={wp.cockpitCalendarEventsWidget?.link}>
                {t('cockpit_home_all', 'Alle anzeigen')}
              </CardLink>
            </div>
            {visits.length === 0 ? (
              <p className="mu">
                {t('cockpit_home_no_visits', 'Keine Besichtigungen geplant.')}
              </p>
            ) : (
              visits.map((e, i) => (
                <div
                  className="item"
                  key={`${e.date}-${i}`}
                >
                  <div>
                    <p className="t">
                      {fmtDate(e.date)} {fmtTime(e.date)}
                    </p>
                    {e.bookedBy ? <p className="mu sm">{e.bookedBy}</p> : null}
                    {isAdvisor && e.bookedByEmail ? (
                      <a
                        className="lnk"
                        href={`mailto:${e.bookedByEmail}`}
                      >
                        {e.bookedByEmail}
                      </a>
                    ) : null}
                    {isAdvisor && e.bookedByPhone ? (
                      <a
                        className="lnk"
                        href={`tel:${e.bookedByPhone}`}
                      >
                        {e.bookedByPhone}
                      </a>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </section>

          <section
            className="card"
            data-widget="cockpitActivities"
          >
            <div className="sec">
              <h2>{t('cockpit_home_activities', 'Letzte Aktivitäten')}</h2>
              <CardLink to={wp.cockpitActivities?.link}>
                {t('cockpit_home_all', 'Alle anzeigen')}
              </CardLink>
            </div>
            {activities.length === 0 ? (
              <p className="mu">
                {t('cockpit_home_no_activities', 'Noch keine Aktivitäten.')}
              </p>
            ) : (
              activities.map((a, i) => (
                <div
                  className="item"
                  key={`${a.date}-${i}`}
                >
                  <div>
                    <p className="t">{getCockpitActivityTitle(t, a.type)}</p>
                    <p className="mu sm">
                      {fmtDate(a.date)}
                      {a.person ? ` · ${a.person}` : ''}
                    </p>
                  </div>
                </div>
              ))
            )}
          </section>
        </div>

        <aside
          className="side"
          data-widget="cockpitExpose"
        >
          {photos[0] ? (
            <img
              className="pic"
              src={photos[0]}
              alt={property.title ?? ''}
            />
          ) : null}
          <section className="card">
            <dl className="dl">
              <div>
                <dt>{t('cockpit_home_price', 'Wunschpreis')}</dt>
                <dd>{fmtChf(property.price)}</dd>
              </div>
              <div>
                <dt>{t('cockpit_home_rooms', 'Zimmer')}</dt>
                <dd>{property.numberOfRooms ?? '–'}</dd>
              </div>
            </dl>
          </section>
          {isAdvisor ? (
            <ContactCard
              title={t('cockpit_home_owner', 'Eigentümer')}
              person={welcome.person}
            />
          ) : (
            <ContactCard
              title={t('cockpit_home_advisor', 'Ihr Berater')}
              person={welcome.consultant}
            />
          )}
        </aside>
      </div>
    </V3Page>
  );
}
